import type { CityConfig } from "../city-template/types";

export const exeterConfig: CityConfig = {
  city: "Exeter",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "exeter",
  route: "/cities/exeter",
  title: "Emergency Passport Exeter, NH | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Exeter, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Seacoast and Rockingham County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Exeter, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Exeter and Seacoast New Hampshire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Historic%20Exeter%20New%20Hampshire%20downtown%20with%20charming%20brick%20colonial%20buildings%2C%20Phillips%20Exeter%20Academy%20campus%20with%20elegant%20brick%20architecture%2C%20New%20England%20autumn%20scene%20with%20red%20and%20orange%20maple%20trees%2C%20warm%20golden%20hour%20light%2C%20small%20town%20main%20street%20with%20American%20flags%2C%20clean%20photorealistic%20cityscape%2C%20no%20visible%20text%20or%20people%2C%20warm%20earth%20tones%20and%20soft%20sky%2C%20professional%20architectural%20photography&width=1800&height=700&seq=exeter-nh-hero-001&orientation=landscape",
  heroImageAlt: "Historic Exeter New Hampshire downtown with charming colonial architecture and Phillips Exeter Academy",
  geoPosition: "42.9814;-70.9478",
  geoPlacename: "Exeter, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Customs House St, Boston, MA 02103",
    distance: "Located in downtown Boston — approximately 60–75 minutes from Exeter via I-95",
  },
  nearbyCitiesZips: [
    { name: "Exeter", zips: "03833" },
    { name: "Stratham", zips: "03885" },
    { name: "Hampton", zips: "03842–03843" },
    { name: "Newmarket", zips: "03857" },
    { name: "Epping", zips: "03042" },
    { name: "Brentwood", zips: "03833" },
    { name: "Kensington", zips: "03833" },
    { name: "Kingston", zips: "03848" },
    { name: "Sandown", zips: "03873" },
    { name: "Fremont", zips: "03044" },
    { name: "East Kingston", zips: "03827" },
    { name: "South Hampton", zips: "03827" },
    { name: "Seabrook", zips: "03874" },
    { name: "North Hampton", zips: "03862" },
    { name: "Rye", zips: "03870" },
    { name: "Greenland", zips: "03840" },
    { name: "Raymond", zips: "03077" },
    { name: "Danville", zips: "03819" },
    { name: "Nottingham", zips: "03290" },
    { name: "Lee", zips: "03861" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Seacoast New Hampshire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Portsmouth", route: "/cities/portsmouth" },
    { name: "Manchester", route: "/cities/manchester" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Dover", route: "/cities/dover" },
    { name: "Hampton", route: "/cities/hampton" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Concord", route: "/cities/concord-nh" },
    { name: "Keene", route: "/cities/keene" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Exeter remotely via secure courier. We do not have a walk-in office in Exeter — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Seacoast New Hampshire travelers.",
  fpcOfficeDistance: "Serving all of Rockingham County and the Seacoast region",
  serviceAreaIntro: "From downtown Exeter's historic Water Street to the neighborhoods near Phillips Exeter Academy, from the Stratham border to the Hampton Falls line — Fast Passport Center serves the entire Exeter community. We regularly help travelers from Stratham, Hampton, Newmarket, and Epping get their passports fast.",
  nearbyCitiesIntro: "We serve all Exeter neighborhoods and nearby Seacoast New Hampshire cities including Stratham, Hampton, Newmarket, Epping, Brentwood, Kensington, Kingston, Sandown, Fremont, East Kingston, Seabrook, North Hampton, Rye, and Greenland. If you are in the greater Exeter area, we have you covered.",
  howItWorksIntro: "Most Exeter travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Portsmouth International Airport nearby and easy access to Boston Logan, Seacoast New Hampshire travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Exeter and Seacoast New Hampshire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Exeter? Here are the most common ones we hear from Seacoast New Hampshire travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Exeter international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Elizabeth M.",
      location: "Exeter, NH",
      rating: 5,
      text: "My daughter's school trip to France was in 4 days and her passport was expired. Fast Passport Center got it renewed in 2 days. Saved the entire trip!",
      service: "Emergency Passport",
    },
    {
      name: "James H.",
      location: "Stratham, NH",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was incredibly smooth, they reviewed everything, and I had my passport in 3 days. Outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah K.",
      location: "Hampton, NH",
      rating: 5,
      text: "Lost my passport the week before my cruise to the Caribbean. They walked me through the DS-64 process and got me a replacement in 48 hours. Amazing service.",
      service: "Lost Passport",
    },
    {
      name: "Robert T.",
      location: "Newmarket, NH",
      rating: 5,
      text: "As a frequent international traveler from the Seacoast, I can't afford passport delays. Fast Passport Center has been my go-to for years. Always reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "exeter-nh-1",
      name: "Exeter Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "30 Portsmouth Ave",
      city: "Exeter",
      state: "NH",
      zip: "03833",
      phone: "(603) 772-6401",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Exeter",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Exeter. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "exeter-nh-2",
      name: "Stratham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "76 Portsmouth Ave",
      city: "Stratham",
      state: "NH",
      zip: "03885",
      phone: "(603) 772-5004",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.3 miles from downtown Stratham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Stratham residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "exeter-nh-3",
      name: "Newmarket Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "126 Main St",
      city: "Newmarket",
      state: "NH",
      zip: "03857",
      phone: "(603) 659-2211",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.2 miles from downtown Newmarket",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Lamprey River Valley residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "exeter-nh-4",
      name: "Epping Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "37 Main St",
      city: "Epping",
      state: "NH",
      zip: "03042",
      phone: "(603) 679-5488",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.4 miles from downtown Epping",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western Rockingham County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "exeter-nh-5",
      name: "Hampton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "55 High St",
      city: "Hampton",
      state: "NH",
      zip: "03842",
      phone: "(603) 926-4915",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.6 miles from downtown Hampton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Seacoast residents. Extended hours available on some days — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "exeter-nh-6",
      name: "Exeter Town Clerk",
      type: "Town Clerk Passport Acceptance",
      address: "10 Front St",
      city: "Exeter",
      state: "NH",
      zip: "03833",
      phone: "(603) 772-1210",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Town Hall",
      website: "https://www.exeternh.gov/",
      websiteLabel: "Exeter Town Clerk",
      notes: "Major municipal facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Exeter photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rockingham County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Exeter applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Exeter families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Exeter travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Exeter." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Exeter address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Exeter before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Exeter for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Seacoast New Hampshire travelers." },
    { q: "How fast can I renew my passport in Exeter?", a: "Fast Passport Center offers fast passport renewal in Exeter in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Exeter in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Exeter?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Exeter residents." },
    { q: "Can I expedite a child passport in Exeter?", a: "Yes. Fast Passport Center expedites child passports in Exeter for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Exeter with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Exeter?", a: "With Fast Passport Center's emergency courier service, Exeter travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Exeter?", a: "Popular options include the Exeter Post Office (03833), Stratham Post Office (03885), Newmarket Post Office (03857), and Exeter Town Clerk (03833). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Exeter?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Stratham or Hampton?", a: "We serve all of the Seacoast region including Stratham, Hampton, Newmarket, Epping, Brentwood, Kensington, Kingston, Sandown, Fremont, Seabrook, North Hampton, Rye, and Greenland. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Exeter?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Exeter", address: "40 Portsmouth Ave, Exeter, NH 03833", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Exeter", address: "130 Portsmouth Ave, Exeter, NH 03833", hours: "Mon–Sun: 7AM–11PM" },
    { name: "UPS Store — Exeter", address: "1 Hampton Rd, Exeter, NH 03833", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Epping", address: "51 Calef Hwy, Epping, NH 03042", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Hampton", address: "725 Lafayette Rd, Hampton, NH 03842", hours: "Mon–Sun: 8AM–10PM" },
    { name: "FedEx Office — Portsmouth", address: "100 Deer St, Portsmouth, NH 03801", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "Costco — Portsmouth", address: "100 Durgin Ln, Portsmouth, NH 03801", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
    { name: "Walgreens — Stratham", address: "76 Portsmouth Ave, Stratham, NH 03885", hours: "Mon–Sun: 7AM–11PM" },
  ],
  dmvLocations: [
    { name: "DMV — Epping", address: "101 Calef Hwy, Epping, NH 03042", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Portsmouth", address: "127 Pembroke Rd, Portsmouth, NH 03801", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Manchester", address: "23 Hazard Ave, Manchester, NH 03109", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for southern New Hampshire residents.", featured: false },
    { name: "DMV — Salem", address: "23 S Broadway, Salem, NH 03079", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: true },
  ],
  airports: [
    { code: "PSM", name: "Portsmouth International Airport", distance: "12 miles from Exeter", address: "50_terminal Way, Portsmouth, NH 03801", note: "Regional commercial airport with seasonal flights to Florida and international destinations. Closest commercial airport for Exeter travelers.", flightUrl: "https://www.flypsm.com/", flightLabel: "PSM Flight Info", featured: true },
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "35 miles from Exeter", address: "1 Airport Rd, Manchester, NH 03103", note: "Major commercial airport for Exeter travelers. International flights to Canada and Europe. Major domestic hub.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: false },
    { code: "BOS", name: "Boston Logan International Airport", distance: "55 miles from Exeter", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-95.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
  ],
};